import React from 'react';
import { useTranslation } from 'react-i18next';

const GAME_TYPES = ['coinflip', 'battle', 'blackjack'];

const TYPE_META = {
    coinflip: { icon: '⚡', accent: 'text-cyber-purple border-cyber-purple/60 bg-cyber-purple/10' },
    battle:   { icon: '⚔', accent: 'text-cyber-pink border-cyber-pink/60 bg-cyber-pink/10' },
    blackjack:{ icon: '🃏', accent: 'text-cyber-cyan border-cyber-cyan/60 bg-cyber-cyan/10' },
};

function TypeButton({ type, active, onClick, disabled }) {
    const { t } = useTranslation();
    const meta = TYPE_META[type] || TYPE_META.coinflip;
    const inactive = 'border-cyber-border text-cyber-dim hover:text-cyber-text';
    return (
        <button
            type="button"
            onClick={() => onClick(type)}
            disabled={disabled}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 rounded-md border transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed ${
                active ? meta.accent : inactive
            }`}
        >
            <span className="text-base leading-none">{meta.icon}</span>
            <span className="text-[9px] font-display tracking-widest uppercase">{t(`game.type.${type}`)}</span>
        </button>
    );
}

export default function LobbySidebar({
    player,
    online,
    selectedType,
    setSelectedType,
    bet,
    changeBet,
    createRoom,
    lobbyFilter,
    setLobbyFilter,
    typeCounts, // { coinflip:n, battle:n, blackjack:n, all:n }
}) {
    const { t } = useTranslation();
    const parsedBet = parseInt(bet) || 0;
    const balance = player.balance ?? 0;
    const canCreate = parsedBet > 0 && parsedBet <= balance;

    const onSubmitCreate = (e) => {
        e?.preventDefault?.();
        if (!canCreate) return;
        createRoom(selectedType);
    };

    return (
        <aside className="space-y-3 lg:sticky lg:top-3 lg:self-start">
            {/* Create room */}
            <section className="neon-panel p-3">
                <h2 className="text-[11px] sm:text-xs font-display font-bold text-white glow-cyan mb-2.5 tracking-widest">
                    {t('game.createRoom')}
                </h2>

                <form onSubmit={onSubmitCreate} className="space-y-3">
                    <div>
                        <label className="neon-label block mb-1.5">{t('game.gameTypeLabel')}</label>
                        <div className="grid grid-cols-3 gap-1.5">
                            {GAME_TYPES.map((type) => (
                                <TypeButton
                                    key={type}
                                    type={type}
                                    active={selectedType === type}
                                    onClick={setSelectedType}
                                />
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="neon-label block mb-1.5">{t('game.wagerAmount')}</label>
                        <input
                            type="number"
                            min={1}
                            max={balance}
                            value={bet || ''}
                            onChange={changeBet}
                            placeholder={t('game.wagerPlaceholder')}
                            className="neon-input w-full !py-2 text-sm"
                        />
                        <div className="text-[10px] text-cyber-dim tracking-widest mt-1 flex justify-between">
                            <span>{t('game.availableLabel')}</span>
                            <span className="text-cyber-purple glow-purple font-mono">{balance}¢</span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={!canCreate}
                        className={`neon-btn neon-btn-purple w-full py-2 text-xs tracking-widest ${
                            !canCreate ? 'opacity-40 cursor-not-allowed' : ''
                        }`}
                    >
                        {t('game.deployRoom')}
                    </button>
                    {parsedBet > balance && (
                        <div className="text-[10px] text-cyber-pink tracking-widest text-center">
                            {t('notify.not_enough_money')}
                        </div>
                    )}
                </form>
            </section>

            {/* Lobby filter */}
            <section className="neon-panel p-3">
                <h2 className="text-[11px] sm:text-xs font-display font-bold text-white glow-cyan mb-2.5 tracking-widest">
                    {t('game.filterLobby')}
                </h2>
                <div className="space-y-1">
                    <FilterRow
                        label={t('game.allTypes')}
                        active={lobbyFilter === 'all'}
                        count={typeCounts.all}
                        onClick={() => setLobbyFilter('all')}
                    />
                    {GAME_TYPES.map((type) => (
                        <FilterRow
                            key={type}
                            label={`${TYPE_META[type].icon} ${t(`game.type.${type}`)}`}
                            active={lobbyFilter === type}
                            count={typeCounts[type] || 0}
                            onClick={() => setLobbyFilter(type)}
                        />
                    ))}
                </div>
                <div className="mt-3 pt-2 border-t border-cyber-border/30 flex items-center gap-2 text-[10px] text-cyber-dim tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan animate-pulse" />
                    {t('game.online', { count: online })}
                </div>
            </section>
        </aside>
    );
}

function FilterRow({ label, active, count, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md border transition-all duration-200 text-left text-[11px] font-display tracking-widest ${
                active
                    ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                    : 'border-cyber-border text-cyber-dim hover:text-cyber-text hover:border-cyber-border'
            }`}
        >
            <span>{label}</span>
            <span className="font-mono opacity-80">{count}</span>
        </button>
    );
}
